import React, { useEffect, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { FiSettings } from "react-icons/fi";
import { useParams } from "react-router-dom";

import axiosInstance from "../../../axios/axios";

import {
  Encrypt_Value,
  Decrypt_Value,
} from "../../../MiddleWare/EncryptDecrypt";

import { exportCareSummaryPdf } from "../../../utils/exportCareSummaryPdf";

import AllergiesCard from "./AllergiesCard";
import MedicationsCard from "./MedicationsCard";
import RecentAppointmentsCard from "./RecentAppointmentsCard";
import HealthScreeningCard from "./HealthScreeningCard";
import VitalSignsCard from "./VitalSignsCard";
import ImmunizationsCard from "./ImmunizationsCard";
import ProceduresCard from "./ProceduresCard";
import SocialHistoryCard from "./SocialHistoryCard";
import RecentLabResultsCard from "./RecentLabResultsCard";
import AlertsClinicalWarningsCard from "./AlertsClinicalWarningsCard";
import FamilyHistoryCard from "./FamilyHistoryCard";
import CarePlanCard from "./CarePlanCard";
import CareTeamCard from "./CareTeamCard";
import TreatmentPlanCard from "./TreatmentPlanCard";
import ProblemsCard from "./ProblemsCard";
import ConfigureSummaryModal, {
  DEFAULT_TOGGLES,
} from "./ConfigureSummaryModal";

const CareSummaryWrapper = () => {
  const { pid } = useParams();
  /* ── Configure Summary Modal State ──────────────────────────────── */
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configToggles, setConfigToggles] = useState(DEFAULT_TOGGLES);
  const [configLoading, setConfigLoading] = useState(false);

  /* STORE ALL EXPORT ARRAYS */

  const [exportData, setExportData] = useState({});
  const [patientDetails, setPatientDetails] = useState({});

  /* PATIENT DETAILS */

  /* COMMON FUNCTION */

  const handleExportData = (key, data) => {
    setExportData((prev) => ({
      ...prev,
      [key]: data,
    }));
  };

  // const handleConfigSave = () => {
  //   setConfigLoading(true);
  //   // Simulate save — replace with actual API call if needed
  //   setTimeout(() => {
  //     setConfigLoading(false);
  //     setShowConfigModal(false);
  //   }, 400);
  // };

  const handleConfigSave = async () => {
    try {
      setConfigLoading(true);

      const payload = {
        patientId: pid,

        care_summary_config: configToggles,
      };

      const encryptedPayload = Encrypt_Value(payload, "vozo");

      const response = await axiosInstance.post(
        "vozo/patient/update-care-summary-config",
        encryptedPayload,
      );

      const decryptedData = Decrypt_Value(response.data, "vozo");
      if (decryptedData?.care_summary_config) {
        setConfigToggles(decryptedData.care_summary_config);
      }

      setExportData((prev) => ({
        ...prev,
        patientSummary: {
          ...prev.patientSummary,
          care_summary_config:
            decryptedData?.care_summary_config || configToggles,
        },
      }));

      setShowConfigModal(false);
    } catch (error) {
      console.log("CONFIG SAVE ERROR", error);
    } finally {
      setConfigLoading(false);
    }
  };

  /* FETCH PATIENT DETAILS */

  const fetchPatientDetails = async () => {
    try {
      const payload = {
        patientId: pid,
      };

      const encryptedPayload = Encrypt_Value(payload, "vozo");

      const response = await axiosInstance.post(
        "vozo/patient/patient-care-summary",
        encryptedPayload,
      );

      const decryptedData = Decrypt_Value(response.data, "vozo");

      if (decryptedData?.care_summary_config) {
        setConfigToggles(decryptedData.care_summary_config);
      }

      const formattedPatientDetails = {
        patient_name: decryptedData?.patient_name || "-",

        pid: decryptedData?.pid || "-",

        dob: decryptedData?.dob || "-",

        mobile_no: decryptedData?.mobile_no || "-",

        email: decryptedData?.email || "-",

        primary_provider: decryptedData?.primary_provider || "-",

        address: decryptedData?.address || "-",
        care_summary_config: decryptedData?.care_summary_config || {},
      };

      setPatientDetails(formattedPatientDetails);

      /* SEND TO PDF EXPORT */

      setExportData((prev) => ({
        ...prev,

        patientSummary: formattedPatientDetails,
      }));
    } catch (error) {
      console.log("Patient Details Error", error);
    }
  };

  useEffect(() => {
    if (pid) {
      fetchPatientDetails();
    }
  }, [pid]);

  return (
    <>
      <div className="care-summary-top-header">
        <h2 className="care-summary-main-title">Care Summary</h2>

        <div className="care-summary-action-wrap">
          <div
            className="care-summary-download"
            onClick={() => exportCareSummaryPdf(exportData)}
          >
            <FiDownload className="care-summary-download-icon" />
          </div>

          <div
            className="care-summary-configure"
            onClick={() => setShowConfigModal(true)}
          >
            <FiSettings className="care-summary-configure-icon" />

            <span>Configure Summary</span>
          </div>
        </div>
      </div>

      <div className="care-summary-grid">
        {configToggles["Problems"] && (
          <ProblemsCard
            onExportData={(data) => handleExportData("problems", data)}
          />
        )}

        {configToggles["Allergies"] && (
          <AllergiesCard
            onExportData={(data) => handleExportData("allergies", data)}
          />
        )}

        {configToggles["Medications"] && (
          <MedicationsCard
            onExportData={(data) => handleExportData("medications", data)}
          />
        )}

        {configToggles["Recent Appointments"] && (
          <RecentAppointmentsCard
            onExportData={(data) => handleExportData("appointments", data)}
          />
        )}

        {configToggles["Health Screening"] && (
          <HealthScreeningCard
            onExportData={(data) => handleExportData("screenings", data)}
          />
        )}

        {configToggles["Vitals"] && (
          <VitalSignsCard
            onExportData={(data) => handleExportData("vitals", data)}
          />
        )}

        {configToggles["Immunizations"] && (
          <ImmunizationsCard
            onExportData={(data) => handleExportData("immunizations", data)}
          />
        )}

        {configToggles["Procedure & Surgeries"] && (
          <ProceduresCard
            onExportData={(data) => handleExportData("procedures", data)}
          />
        )}

        {configToggles["Lab Results"] && (
          <RecentLabResultsCard
            onExportData={(data) => handleExportData("labs", data)}
          />
        )}

        {configToggles["Social History"] && (
          <SocialHistoryCard
            onExportData={(data) => handleExportData("socialHistory", data)}
          />
        )}

        {configToggles["Family History"] && (
          <FamilyHistoryCard
            onExportData={(data) => handleExportData("familyHistory", data)}
          />
        )}

        {configToggles["Alerts & Clinical Warning"] && (
          <AlertsClinicalWarningsCard
            onExportData={(data) => handleExportData("alerts", data)}
          />
        )}

        {configToggles["Treatment Plan"] && (
          <TreatmentPlanCard
            onExportData={(data) => handleExportData("treatmentPlans", data)}
          />
        )}

        {configToggles["Care Plan"] && (
          <CarePlanCard
            onExportData={(data) => handleExportData("carePlans", data)}
          />
        )}

        {configToggles["Care Team"] && (
          <CareTeamCard
            onExportData={(data) => handleExportData("careTeams", data)}
          />
        )}
      </div>

      <ConfigureSummaryModal
        show={showConfigModal}
        onHide={() => setShowConfigModal(false)}
        configToggles={configToggles}
        setConfigToggles={setConfigToggles}
        onSave={handleConfigSave}
        loading={configLoading}
      />
    </>
  );
};

export default React.memo(CareSummaryWrapper);
